import { supabase } from '@/lib/supabase'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string

// Convert a URL-safe base64 VAPID key to a Uint8Array (needed by PushManager).
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

export function isSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !!VAPID_PUBLIC_KEY
  )
}

export async function getStatus(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!isSupported()) return 'unsupported'
  return Notification.permission as 'granted' | 'denied' | 'default'
}

export async function subscribe(): Promise<boolean> {
  if (!isSupported()) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const reg = await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    })
  }

  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return false

  const json = sub.toJSON()
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: user.id,
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    { onConflict: 'user_id,endpoint' }
  )

  return !error
}

export async function unsubscribe(): Promise<void> {
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (sub) {
    // Remove from DB first
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('endpoint', sub.endpoint)
    await sub.unsubscribe()
  }
}

// Fire-and-forget push notification. Call after a successful mutation.
// Never blocks the UI or throws.
export function notifyActivityChange(params: {
  type: 'created' | 'updated' | 'deleted'
  activityId: string
  activityTitle: string
  tourId: string
  orgId: string
  visibility: 'public' | 'assigned'
  venueName?: string
  startTime?: string
  changedBy: string
}): void {
  supabase.functions.invoke('push-notify', { body: params }).catch(() => {})
}
